import { IonIcon } from '@ionic/react';
import { lockClosed } from 'ionicons/icons';
import { useMemo, useState } from 'react';
import { isoWeekStart, type IsoDate } from '../../domain/dates';
import { formatDate, formatPrice, recipeName } from '../i18n/format';
import { useMessages } from '../i18n/LanguageProvider';
import { FoodPlate } from '../pictures/FoodPlate';
import { recipePicture } from '../pictures/pictures';
import { recipeById } from '../recipes/recipe-lookup';
import type { StoredPlan } from '../state/app-data';
import { useAppData } from '../state/AppDataProvider';
import { planView, warningText, type DayRow, type PlanView } from './plan-view';

function BudgetBar({ budget }: { budget: PlanView['budget'] }) {
  const { language, t } = useMessages();
  const share = Math.min(1, budget.planned / budget.limit);
  const over = budget.planned > budget.limit;
  return (
    <section className={`app-card${over ? ' app-budget-over' : ''}`}>
      <p className="app-lead">
        {t.budgetPlanned(
          formatPrice(budget.planned, language, true),
          formatPrice(budget.limit, language, false),
        )}
      </p>
      <div className="app-budget">
        <div className="app-budget-fill" style={{ width: `${share * 100}%` }} />
      </div>
    </section>
  );
}

function Warnings({ warnings }: { warnings: PlanView['warnings'] }) {
  const { language } = useMessages();
  return warnings.map((warning) => {
    const { day, text } = warningText(warning, language);
    return (
      <p key={`${warning.code}-${warning.date ?? ''}`} className="app-hint">
        {day && <strong>{day}</strong>} <span>{text}</span>
      </p>
    );
  });
}

function DayButton({ day, onSelect }: { day: DayRow; onSelect: (date: IsoDate) => void }) {
  const { language, t } = useMessages();
  return (
    <li>
      <button
        type="button"
        className={`app-row${day.past ? ' app-past' : ''}`}
        onClick={() => onSelect(day.date)}
      >
        <FoodPlate picture={recipePicture(day.recipeId)} size={40} />
        <span className="app-row-main">
          <span className="app-muted">{formatDate(day.date, language, 'short')}</span>
          <span>{recipeName(recipeById(day.recipeId), language)}</span>
          <span className="app-chips">
            <span className="app-chip">{t.minutes(day.totalMinutes)}</span>
            {day.fish && <span className="app-chip app-chip-fish">{t.fish}</span>}
            {day.quick && <span className="app-chip">{t.quick}</span>}
            {day.lunchbox && <span className="app-chip">{t.lunchbox}</span>}
          </span>
        </span>
        {day.locked && <IonIcon icon={lockClosed} aria-label={t.locked} role="img" />}
      </button>
    </li>
  );
}

/**
 * DE: Ein Plan: Budgetbalken, Hinweise und die Tage nach Wochen (ganzer Plan oder diese Woche).
 * EN: One plan: budget bar, warnings and the days by week (whole plan or this week).
 */
export function PlanSection({
  stored,
  onSelect,
}: {
  stored: StoredPlan;
  onSelect: (date: IsoDate) => void;
}) {
  const { data, today } = useAppData();
  const { language, t } = useMessages();
  const view = useMemo(() => planView(stored, data, today), [stored, data, today]);
  const [onlyThisWeek, setOnlyThisWeek] = useState(false);
  const thisWeek = view.weeks.find((week) => week.start === isoWeekStart(today)) ?? view.weeks[0];
  const weeks = onlyThisWeek && thisWeek ? [thisWeek] : view.weeks;
  return (
    <>
      <BudgetBar budget={view.budget} />
      <Warnings warnings={view.warnings} />
      <div className="app-toggle">
        <button type="button" aria-pressed={!onlyThisWeek} onClick={() => setOnlyThisWeek(false)}>
          {t.wholePlan}
        </button>
        <button type="button" aria-pressed={onlyThisWeek} onClick={() => setOnlyThisWeek(true)}>
          {t.thisWeek}
        </button>
      </div>
      {weeks.map((week) => (
        <section key={week.start} className="app-card">
          <h2>
            {t.weekOf(
              formatDate(week.start, language, 'short'),
              formatPrice(week.cost, language, true),
            )}
          </h2>
          <ul className="app-rows">
            {week.days.map((day) => (
              <DayButton key={day.date} day={day} onSelect={onSelect} />
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}
